import React, { useEffect, useRef } from 'react';

// Cloudflare Turnstile widget.
//
// REACT_APP_TURNSTILE_SITE_KEY set     -> real widget.
// no site key, development build       -> stub token (matches TURNSTILE_MODE=stub on the Edge Function).
// no site key, production build        -> blocked, unless REACT_APP_TURNSTILE_STUB=true (staging only).
export const STUB_TOKEN = 'stub-pass';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
export const isStub = () =>
  !siteKey && (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_TURNSTILE_STUB === 'true');

let scriptPromise;
const loadScript = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = SCRIPT_SRC;
      el.async = true;
      el.onload = () => resolve(window.turnstile);
      el.onerror = () => {
        scriptPromise = undefined;
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(el);
    });
  }
  return scriptPromise;
};

// `resetSignal` changes (e.g. increments) after each submit: tokens are single use, so the widget is reset.
const Turnstile = ({ onToken, resetSignal = 0 }) => {
  const container = useRef(null);
  const widgetId = useRef(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (siteKey) return undefined;
    if (isStub()) onTokenRef.current(STUB_TOKEN);
    return undefined;
  }, [resetSignal]);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadScript()
      .then((turnstile) => {
        if (cancelled || !container.current) return;
        widgetId.current = turnstile.render(container.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => onTokenRef.current('')
        });
      })
      .catch(() => onTokenRef.current(''));
    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, []);

  useEffect(() => {
    if (resetSignal > 0 && widgetId.current !== null && window.turnstile) {
      onTokenRef.current('');
      window.turnstile.reset(widgetId.current);
    }
  }, [resetSignal]);

  if (!siteKey && !isStub()) {
    return <p className="alert alert-error" role="alert">Spam protection is not configured, so this form is unavailable. Please contact the school directly.</p>;
  }
  return siteKey ? <div ref={container} className="turnstile-widget" /> : null;
};

export default Turnstile;
